import { all, call, fork, put, select, takeLatest } from 'redux-saga/effects';

import { getOrCreateVisitorId } from '@/core/tracking/utils/visitorId';
import { authSelector } from '@/customerManagement/customerManagement.selectors';
import {
  customerProductPreferencesSelector,
  type CustomerProductPreferencesSelector,
} from '@/customerManagement/customerProductPreferences/selectors';
import { AlgoliaSearchInsights } from '@/productDiscovery/algolia/searchInsights/AlgoliaSearchInsights';

import type { CustomAlgoliaSearchResults } from '../../interfaces/algolia';
import { processSearchResult } from '../../services/algolia/processor';
import { algoliaSearchFromRecommendation } from '../../services/algolia/proxy.client';
import { createPlaylistContainer } from '../../services/algolia/utils/createPlaylistContainer/createPlaylistContainer';
import { CONTENT_TYPE_MS_RECO, ERROR_MESSAGE } from './constants';
import {
  FETCH_RECOMMENDATION,
  FETCH_RECOMMENDATION_CONFIG_SUCCESS,
  fetchProductsRecommendation,
  fetchProductsRecommendationFailed,
  fetchProductsRecommendationSuccess,
  type FetchRecommendation,
  type FetchRecommendationConfigSuccess,
} from './ProductRecommendation.actions';

export function* getProductsForRecommendation(
  action: FetchRecommendationConfigSuccess | FetchRecommendation,
) {
  try {
    yield put(fetchProductsRecommendation());
    if (action.config.length === 0) throw new Error(ERROR_MESSAGE.EMPTY_CONFIG);

    // Recommendations filtered by type (exclude unused types)
    const configFiltered = action.config.filter((reco) =>
      Object.values(CONTENT_TYPE_MS_RECO).includes(reco.displayType),
    );
    const { userId } = yield select(authSelector);
    const visitorId: string = yield call(getOrCreateVisitorId);
    const userToken: string = userId
      ? yield call(AlgoliaSearchInsights.calculateUserIdHash, String(userId))
      : visitorId;

    const algoliaResults: CustomAlgoliaSearchResults[] = yield call(
      algoliaSearchFromRecommendation,
      configFiltered,
      userToken,
    );

    const processedData = processSearchResult(algoliaResults);

    const { purchasedTwice } = (yield select(
      customerProductPreferencesSelector,
    )) as CustomerProductPreferencesSelector;

    const playlists = createPlaylistContainer(
      configFiltered,
      processedData,
      purchasedTwice,
    );
    yield put(fetchProductsRecommendationSuccess(playlists));
  } catch (error) {
    yield put(fetchProductsRecommendationFailed(error as Error));
  }
}

// WATCHER: to get products for recommendation
export function* watchFetchRecommendationConfigSuccess() {
  yield takeLatest(
    [FETCH_RECOMMENDATION_CONFIG_SUCCESS, FETCH_RECOMMENDATION],
    getProductsForRecommendation,
  );
}

export default function* rootRecommendationSaga() {
  yield all([fork(watchFetchRecommendationConfigSuccess)]);
}
